import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CELL_DOOR_OFF, ownerHubDoorOrigin, ownerHubToken } from '@/lib/cell/ownerHub';
import { useSession } from '@/lib/auth';
import { useActiveLocationId } from '@/lib/locationContext';
import { useFeature } from '@/lib/useFeature';
import { viewAsProId } from '@/lib/viewAs';
import { mediaOrigin } from '@/lib/hosts';

/**
 * YOUR SIGNATURE — the pro's profile-of-record (SIGNATURE S1, WI 2d50654c).
 *
 * Peter approved the Erica mockup built from her real records and said "go, build S1 with the
 * mockup as the spec" (dev.cadenceadvisers.com/design/pro-signature-erica-mockup.html). This
 * page IS that mockup, live: what the platform has observed about the pro's style, voice,
 * content mix and audience — read from their own Instagram by the /pro/signature door — plus
 * the layer THEY declare (confirmed style words, hard "never do" lines, their goal), which
 * outranks everything observed.
 *
 * Postures the mockup fixed:
 *   · everything visible — the pro sees all we know, and can correct any of it;
 *   · honest states — no readable Instagram gets an invitation, never a padded page;
 *   · goal is infer-then-confirm — "looks like X, right?" beats an empty form;
 *   · the derivation is the door's business — this page never spends on its own.
 */

// CELL (M28): the Owner Hub door for this build — doorOrigin() in production (lib/hosts.ts), the AWS staging host on
// the cell build; null there = not on the AWS staging build yet, and the door helper below says so (CELL_DOOR_OFF).
const DOOR = ownerHubDoorOrigin();
const MEDIA = mediaOrigin(); // CELL (M28): the media copy on the cell build, the Owner Hub host otherwise

type PostRef = { url: string; likes: number; comments: number };
type Derived = {
  styleLead: string;
  phraseBank?: string[];
  trustSignals?: string[];
  uvp?: string;
  problem?: string;
  palette: { hex: string; name: string }[];
  modes: string;
  voiceLead: string;
  quotes: { text: string; why: string }[];
  voiceChips: string[];
  mix: { label: string; count: number }[];
  services: string[];
  rewards: { context: string; insight: string; topPost: PostRef | null };
  story: { when: string; what: string }[];
  goalInference: { looksLike: string; why: string };
  styleWords: string[];
  evidence: (PostRef & { label: string })[];
};
type Declared = { words: string[]; neverDo: string[]; siteUrl?: string };
type Goal = { status: 'proposed' | 'confirmed' | 'custom'; text: string };
type DoorPayload = {
  ok: boolean; error?: string;
  state: 'ready' | 'invite' | 'none';
  derived?: Derived; declared?: Declared; goal?: Goal; derivedAt?: string;
  name?: string; business?: string; igHandle?: string;
  stale?: boolean; viewing?: boolean;
};

async function doorPost(body: Record<string, unknown>): Promise<DoorPayload> {
  if (!DOOR) throw new Error(CELL_DOOR_OFF);
  // CELL (M28): this tab's own token — Supabase's in production, Cognito's access token on the cell build (lib/cell/ownerHub.ts).
  const token = await ownerHubToken();
  if (!token) throw new Error('Sign in again — no session.');
  const res = await fetch(`${DOOR}/pro/signature`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const payload = (await res.json().catch(() => null)) as DoorPayload | null;
  if (!res.ok || !payload?.ok) throw new Error(payload?.error ?? 'Could not read your Signature right now.');
  return payload;
}

export default function Signature() {
  const { session } = useSession();
  const userId = session?.user?.id;
  const { activeLocationId } = useActiveLocationId();
  const feature = useFeature('signature');
  const queryClient = useQueryClient();
  /**
   * VIEW-AS RIDES EVERY CALL (Peter's live catch, 2026-08-29: the same photos and rewards
   * showed for every pro in View-their-Hub). Without view_pro the door resolves the
   * VIEWER'S own pro identity, so every viewed pro rendered the viewer's page — and a
   * derivation fired against the viewer's own record. The viewed id is also part of the
   * query key, or pro A's cached page would render for pro B.
   */
  const viewing = viewAsProId();

  const { data, isLoading, error } = useQuery<DoorPayload>({
    queryKey: ['pro-signature', userId, activeLocationId, viewing],
    enabled: !!userId && !!activeLocationId && feature.enabled,
    // The first read after a data change derives server-side and can take ~20s — worth one
    // honest wait, then it is cached until the pro's Instagram moves again.
    staleTime: 5 * 60 * 1000,
    queryFn: () => doorPost({
      act: 'get', location_id: activeLocationId,
      ...(viewing ? { view_pro: viewing } : {}),
    }),
  });

  if (!feature.loading && !feature.enabled) {
    return (
      <div className="card text-sm text-image-charcoal">
        This section isn&rsquo;t switched on for your studio yet.
      </div>
    );
  }
  if (feature.loading || isLoading) {
    return <DerivingCard />;
  }
  if (error || !data) {
    return (
      <div className="card text-sm text-image-charcoal">
        Couldn&rsquo;t read your Signature right now — try again in a minute.
      </div>
    );
  }

  // A viewed pro with no Signature yet: say so to the VIEWER — the invite's "add your
  // handle in Me" is written for the pro, not for an operator looking over their shoulder.
  if (viewing && (data.state !== 'ready' || !data.derived)) {
    return (
      <div className="card text-sm text-image-charcoal">
        This pro&rsquo;s Signature hasn&rsquo;t been read yet — it builds itself the first
        time they open this tab. Nothing here is shared until then.
      </div>
    );
  }
  if (data.state !== 'ready' || !data.derived) return <InviteState payload={data} />;

  const d = data.derived;
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['pro-signature'] });

  return (
    <div className="space-y-4">
      <div>
        <div className="text-[10px] font-extrabold tracking-[0.14em] text-image-pink uppercase">
          Your Signature
        </div>
        <h1 className="font-display text-2xl font-bold text-image-black">
          This is what we know about you.
        </h1>
        <p className="text-sm text-image-charcoal mt-1">
          Read from your own Instagram and your studio profile — so everything you make here
          sounds like you, not like a template. You can correct any of it.
        </p>
        <div className="inline-flex items-center gap-1.5 mt-2 text-[11px] font-bold text-emerald-700 bg-emerald-50 rounded-full px-3 py-1">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-600" />
          Updates itself when you post{data.stale ? ' · showing your last read' : ''}
        </div>
      </div>

      {d.styleLead && (
        <section className="card">
          <SectionHead>Your style</SectionHead>
          <p className="text-sm font-semibold text-image-black leading-relaxed">{d.styleLead}</p>
          {d.palette.length > 0 && (
            <div className="flex gap-2 mt-3" data-palette>
              {d.palette.map((p) => (
                <span key={p.hex} title={p.name} className="w-8 h-8 rounded-full border-2 border-white shadow"
                  style={{ backgroundColor: p.hex }} />
              ))}
            </div>
          )}
          {d.palette.length > 0 && (
            <div className="text-[11px] text-image-slate mt-1.5">
              Pulled from your own posts — {d.palette.map((p) => p.name).filter(Boolean).join(' · ')}
            </div>
          )}
          {d.modes && <p className="text-[13px] text-image-charcoal mt-3 leading-relaxed">{d.modes}</p>}
          {d.evidence.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 mt-3">
              {d.evidence.map((e) => (
                <figure key={e.url}>
                  <img src={`${MEDIA}${e.url}`} alt={e.label} loading="lazy"
                    className="w-full aspect-[4/5] object-cover rounded-lg border border-image-fog" />
                  <figcaption className="text-[10px] text-image-slate mt-1 leading-snug">{e.label}</figcaption>
                </figure>
              ))}
            </div>
          )}
        </section>
      )}

      {(d.voiceLead || d.quotes.length > 0) && (
        <section className="card">
          <SectionHead>Your voice</SectionHead>
          {d.voiceLead && <p className="text-sm font-semibold text-image-black leading-relaxed">{d.voiceLead}</p>}
          {d.quotes.map((q) => (
            <blockquote key={q.text}
              className="border-l-[3px] border-image-pink/60 bg-image-fog/60 rounded-r-lg px-3.5 py-2.5 mt-2.5">
              <p className="text-[13px] italic leading-relaxed">&ldquo;{q.text}&rdquo;</p>
              {q.why && (
                <div className="text-[10px] font-bold uppercase tracking-wide text-image-slate mt-1">{q.why}</div>
              )}
            </blockquote>
          ))}
          {d.voiceChips.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3">
              {d.voiceChips.map((c) => (
                <span key={c} className="text-[11px] font-bold bg-pink-50 text-image-pink border border-pink-200 rounded-full px-3 py-1">{c}</span>
              ))}
            </div>
          )}
        </section>
      )}

      {((d.phraseBank?.length ?? 0) > 0 || (d.trustSignals?.length ?? 0) > 0 || d.uvp) && (
        <section className="card">
          <SectionHead>Your words, ready to use</SectionHead>
          {d.uvp && <p className="text-sm font-semibold text-image-black leading-relaxed">{d.uvp}</p>}
          {d.problem && <p className="text-[13px] text-image-charcoal mt-1.5 leading-relaxed">{d.problem}</p>}
          {(d.phraseBank?.length ?? 0) > 0 && (
            <div className="flex flex-wrap gap-2 mt-3">
              {d.phraseBank!.map((w) => (
                <span key={w} className="text-[11px] font-bold bg-image-fog border border-image-fog rounded-full px-3 py-1">&ldquo;{w}&rdquo;</span>
              ))}
            </div>
          )}
          {(d.trustSignals?.length ?? 0) > 0 && (
            <ul className="mt-3 space-y-1">
              {d.trustSignals!.map((t) => (
                <li key={t} className="text-xs text-image-charcoal flex gap-2"><span className="text-image-pink font-bold">{'\u2713'}</span>{t}</li>
              ))}
            </ul>
          )}
          <p className="text-[11px] text-image-slate mt-3">
            Your own phrases and provable claims — everything we make for you can reach for these.
          </p>
        </section>
      )}

      {d.mix.length > 0 && (
        <section className="card">
          <SectionHead>What you post</SectionHead>
          <MixBars mix={d.mix} />
          {d.services.length > 0 && (
            <p className="text-xs text-image-charcoal mt-3">
              Services we can see you doing: {d.services.join(' · ')}
            </p>
          )}
        </section>
      )}

      {(d.rewards.topPost || d.rewards.insight) && (
        <section className="card">
          <SectionHead>What your audience rewards</SectionHead>
          {d.rewards.topPost && (
            <div className="flex items-center gap-3.5 bg-image-black text-white rounded-xl p-3.5">
              <img src={`${MEDIA}${d.rewards.topPost.url}`} alt="Your biggest post" loading="lazy"
                className="w-14 h-[70px] object-cover rounded-lg" />
              <div>
                <div className="text-xl font-extrabold leading-none">
                  {d.rewards.topPost.likes.toLocaleString()} likes &middot; {d.rewards.topPost.comments.toLocaleString()} comments
                </div>
                {d.rewards.context && (
                  <div className="text-[11.5px] text-white/75 mt-1.5 leading-relaxed">{d.rewards.context}</div>
                )}
              </div>
            </div>
          )}
          {d.rewards.insight && (
            <div className="bg-pink-50 border border-pink-200 rounded-lg px-3.5 py-2.5 text-[13px] leading-relaxed mt-3">
              {d.rewards.insight}
            </div>
          )}
        </section>
      )}

      {d.story.length > 0 && (
        <section className="card">
          <SectionHead>Your story so far</SectionHead>
          <div className="border-l-2 border-image-fog pl-4 space-y-3">
            {d.story.map((s) => (
              <div key={s.when + s.what} className="relative">
                <span className="absolute -left-[21.5px] top-1.5 w-2 h-2 rounded-full bg-image-pink" />
                <div className="text-[10px] font-bold uppercase tracking-wide text-image-slate">{s.when}</div>
                <div className="text-[13px] leading-relaxed">{s.what}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      <DeclareCard derived={d} declared={data.declared ?? { words: [], neverDo: [], siteUrl: '' }}
        locationId={activeLocationId!} viewing={!!viewing || !!data.viewing} onSaved={refresh} />
      <GoalCard goal={data.goal ?? { status: 'proposed', text: d.goalInference.looksLike }}
        why={d.goalInference.why} locationId={activeLocationId!} viewing={!!viewing || !!data.viewing} onSaved={refresh} />

      <div className="border border-dashed border-image-slate/40 rounded-xl px-4 py-3 text-xs text-image-slate leading-relaxed">
        <b>Yours, and visible.</b> You&rsquo;re looking at everything we know. Correct or remove any
        of it, any time. It re-reads itself after your Instagram updates — never stale, never a
        surprise.
      </div>
    </div>
  );
}

/** The derivation narrated (S2, the Munch lift): each line names a REAL stage of the read —
 *  the engine genuinely looks at their images, reads their captions, and checks their booking
 *  page — so the theater is honest. Pure client-side timing over the one long call. */
const DERIVE_LINES = [
  'Reading your posts and your bio\u2026',
  'Looking at your images — your colors, your look\u2026',
  'Reading how you talk to your clients\u2026',
  'Checking your booking page for your menu and reviews\u2026',
  'Reading what your audience responds to\u2026',
  'Writing up what makes you, you\u2026',
];
function DerivingCard() {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = window.setInterval(() => setI((n) => Math.min(n + 1, DERIVE_LINES.length - 1)), 3500);
    return () => window.clearInterval(t);
  }, []);
  return (
    <div className="card text-sm text-image-charcoal">
      <div className="font-semibold text-image-black mb-2">Building your Signature</div>
      {DERIVE_LINES.slice(0, i + 1).map((l, n) => (
        <div key={l} className={n === i ? 'text-image-black' : 'text-image-slate'}>
          {n < i ? '\u2713' : '\u2022'} {l}
        </div>
      ))}
      <div className="text-[11px] text-image-slate mt-2">First time takes about half a minute.</div>
    </div>
  );
}

function SectionHead({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-2 mb-2.5">
      <span className="w-4 h-[3px] rounded bg-image-pink" />
      <span className="text-[10px] font-extrabold tracking-[0.12em] uppercase text-image-slate">{children}</span>
    </div>
  );
}

function MixBars({ mix }: { mix: { label: string; count: number }[] }) {
  const max = Math.max(...mix.map((m) => m.count), 1);
  const tones = ['bg-image-pink', 'bg-amber-400', 'bg-rose-400', 'bg-pink-300', 'bg-image-slate'];
  return (
    <div className="space-y-2">
      {mix.map((m, i) => (
        <div key={m.label} className="grid grid-cols-[minmax(0,10rem)_1fr_2rem] items-center gap-2.5 text-xs">
          <span className="font-semibold truncate">{m.label}</span>
          <span className="h-2.5 rounded-full bg-image-fog overflow-hidden">
            <span className={`block h-full rounded-full ${tones[i % tones.length]}`}
              style={{ width: `${Math.max(6, (m.count / max) * 100)}%` }} />
          </span>
          <span className="text-right font-bold text-image-slate">{m.count}</span>
        </div>
      ))}
    </div>
  );
}

/**
 * DID WE GET IT RIGHT — the declared layer. What the pro confirms here becomes law: the
 * Designer and every downstream reader honour declared over observed. View-as renders it
 * read-only (a viewer never writes a pro's record — the door refuses too; this is courtesy).
 */
function DeclareCard({ derived, declared, locationId, viewing, onSaved }: {
  derived: Derived; declared: Declared; locationId: string; viewing: boolean; onSaved: () => void;
}) {
  const confirmed = new Set(declared.words);
  const candidates = [...new Set([...derived.styleWords, ...declared.words])];
  const [adding, setAdding] = useState('');
  const [never, setNever] = useState('');
  const [siteInput, setSiteInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');

  const save = async (next: Declared) => {
    setBusy(true); setNote('');
    try {
      await doorPost({
        act: 'declare', location_id: locationId,
        words: next.words, neverDo: next.neverDo, siteUrl: next.siteUrl ?? '',
      });
      setNote('Saved — this holds from now on.');
      onSaved();
    } catch (e) {
      setNote(e instanceof Error ? e.message : 'Could not save — try again.');
    } finally { setBusy(false); }
  };

  const toggle = (w: string) => {
    const words = confirmed.has(w) ? declared.words.filter((x) => x !== w) : [...declared.words, w];
    void save({ ...declared, words });
  };

  return (
    <section className="card">
      <SectionHead>Did we get it right?</SectionHead>
      <p className="text-[13px] leading-relaxed">
        Tap what&rsquo;s true, fix what&rsquo;s not. What you confirm here becomes law — nothing we
        make for you will cross it.
      </p>
      <div className="flex flex-wrap gap-2 mt-3">
        {candidates.map((w) => (
          <button key={w} type="button" disabled={busy || viewing} onClick={() => toggle(w)}
            className={`text-xs font-bold rounded-full px-3.5 py-1.5 border transition-colors ${
              confirmed.has(w)
                ? 'bg-pink-50 border-pink-300 text-image-pink'
                : 'bg-white border-image-fog text-image-charcoal'
            }`}>
            {w}{confirmed.has(w) ? ' ✓' : ''}
          </button>
        ))}
      </div>
      {!viewing && (
        <form className="flex gap-2 mt-3" onSubmit={(e) => {
          e.preventDefault();
          const w = adding.trim();
          if (!w) return;
          setAdding('');
          void save({ ...declared, words: [...declared.words, w] });
        }}>
          <input value={adding} onChange={(e) => setAdding(e.target.value)} maxLength={60}
            placeholder="Add your own words" aria-label="Add your own style words"
            className="flex-1 text-base border border-image-fog rounded-lg px-3 py-2" />
          <button type="submit" disabled={busy || !adding.trim()} className="btn-primary text-xs px-4">Add</button>
        </form>
      )}
      <div className="mt-4">
        <div className="text-[11px] font-bold text-image-charcoal">
          Your booking page or website
        </div>
        <p className="text-[11px] text-image-slate mt-0.5">
          Drop the link (GlossGenius, Vagaro, your site) and your Signature reads your real menu,
          prices and reviews from it.
        </p>
        {declared.siteUrl ? (
          <div className="flex items-center justify-between text-xs bg-image-fog/70 rounded-lg px-3 py-1.5 mt-2">
            <span className="truncate">{declared.siteUrl}</span>
            {!viewing && (
              <button type="button" disabled={busy} aria-label="Remove your site link"
                onClick={() => void save({ ...declared, siteUrl: '' })}
                className="text-image-slate font-bold px-1 shrink-0">{'\u2715'}</button>
            )}
          </div>
        ) : !viewing && (
          <form className="flex gap-2 mt-2" onSubmit={(e) => {
            e.preventDefault();
            const u = siteInput.trim();
            if (!u) return;
            setSiteInput('');
            void save({ ...declared, siteUrl: u });
          }}>
            <input value={siteInput} onChange={(e) => setSiteInput(e.target.value)} maxLength={300}
              placeholder="yourname.glossgenius.com" aria-label="Your booking page or website"
              className="flex-1 text-base border border-image-fog rounded-lg px-3 py-2" />
            <button type="submit" disabled={busy || !siteInput.trim()} className="btn-primary text-xs px-4">Add</button>
          </form>
        )}
      </div>
      <div className="mt-4">
        <div className="text-[11px] font-bold text-image-charcoal">
          Anything we should <span className="text-image-pink">never</span> do?
        </div>
        {declared.neverDo.length > 0 && (
          <ul className="mt-1.5 space-y-1">
            {declared.neverDo.map((n) => (
              <li key={n} className="flex items-center justify-between text-xs bg-image-fog/70 rounded-lg px-3 py-1.5">
                <span>{n}</span>
                {!viewing && (
                  <button type="button" disabled={busy} aria-label={`Remove ${n}`}
                    onClick={() => void save({ ...declared, neverDo: declared.neverDo.filter((x) => x !== n) })}
                    className="text-image-slate font-bold px-1">✕</button>
                )}
              </li>
            ))}
          </ul>
        )}
        {!viewing && (
          <form className="flex gap-2 mt-2" onSubmit={(e) => {
            e.preventDefault();
            const n = never.trim();
            if (!n) return;
            setNever('');
            void save({ ...declared, neverDo: [...declared.neverDo, n] });
          }}>
            <input value={never} onChange={(e) => setNever(e.target.value)} maxLength={60}
              placeholder={'e.g. "never neon", "never show a client’s face"'}
              aria-label="Add a never-do rule"
              className="flex-1 text-base border border-image-fog rounded-lg px-3 py-2" />
            <button type="submit" disabled={busy || !never.trim()} className="btn-primary text-xs px-4">Add</button>
          </form>
        )}
      </div>
      {note && <div className="text-[11px] font-semibold text-image-charcoal mt-2" role="status">{note}</div>}
    </section>
  );
}

/** YOUR GOAL — infer-then-confirm (the mockup's posture): "looks like X — right?" */
function GoalCard({ goal, why, locationId, viewing, onSaved }: {
  goal: Goal; why: string; locationId: string; viewing: boolean; onSaved: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');

  const send = async (body: Record<string, unknown>) => {
    setBusy(true); setNote('');
    try {
      await doorPost({ act: 'goal', location_id: locationId, ...body });
      setEditing(false);
      onSaved();
    } catch (e) {
      setNote(e instanceof Error ? e.message : 'Could not save — try again.');
    } finally { setBusy(false); }
  };

  return (
    <section className="card">
      <SectionHead>Your goal{goal.status === 'proposed' ? ', as we read it' : ''}</SectionHead>
      {goal.status === 'proposed' ? (
        <>
          <p className="text-sm font-semibold text-image-black leading-relaxed">
            It looks like you&rsquo;re focused on <span className="text-image-pink">{goal.text || 'growing your business'}</span>.
          </p>
          {why && <p className="text-xs text-image-charcoal mt-1.5 leading-relaxed">Why we think so: {why}</p>}
          {!viewing && !editing && (
            <div className="flex gap-2.5 mt-3.5">
              <button type="button" disabled={busy} onClick={() => void send({ confirm: true })}
                className="btn-primary flex-1 text-sm">That&rsquo;s my goal</button>
              <button type="button" disabled={busy} onClick={() => setEditing(true)}
                className="flex-1 text-sm font-bold border-[1.5px] border-image-fog rounded-lg py-2.5">
                It&rsquo;s something else
              </button>
            </div>
          )}
        </>
      ) : (
        <>
          <p className="text-sm font-semibold text-image-black leading-relaxed">{goal.text}</p>
          <p className="text-[11px] text-image-slate mt-1">
            Your goal, in your words — it shapes what we suggest.
            {!viewing && (
              <button type="button" className="underline font-bold ml-1.5" onClick={() => { setText(goal.text); setEditing(true); }}>
                Change it
              </button>
            )}
          </p>
        </>
      )}
      {editing && !viewing && (
        <form className="flex gap-2 mt-3" onSubmit={(e) => {
          e.preventDefault();
          if (text.trim()) void send({ text: text.trim() });
        }}>
          <input value={text} onChange={(e) => setText(e.target.value)} maxLength={160} autoFocus
            placeholder="Say it in your own words" aria-label="Your goal in your own words"
            className="flex-1 text-base border border-image-fog rounded-lg px-3 py-2" />
          <button type="submit" disabled={busy || !text.trim()} className="btn-primary text-xs px-4">Save</button>
        </form>
      )}
      {note && <div className="text-[11px] font-semibold text-image-charcoal mt-2" role="status">{note}</div>}
    </section>
  );
}

/** No readable Instagram yet — the page is the reason to add one. Honest, no padding. */
function InviteState({ payload }: { payload: DoorPayload }) {
  return (
    <div className="space-y-4">
      <div>
        <div className="text-[10px] font-extrabold tracking-[0.14em] text-image-pink uppercase">
          Your Signature
        </div>
        <h1 className="font-display text-2xl font-bold text-image-black">
          Let&rsquo;s learn what makes you, you.
        </h1>
      </div>
      <div className="card text-sm text-image-charcoal leading-relaxed space-y-2.5">
        <p>
          Your Signature is the page that knows your style, your voice, and what your audience
          responds to — read from your own posts, so everything we make for you sounds like you.
        </p>
        <p>
          {payload.igHandle
            ? <>We have your handle (@{payload.igHandle}) but no readable posts yet — check back after your next sweep.</>
            : <>Add your Instagram handle in <b>Me</b> and your Signature builds itself from your own work.</>}
        </p>
      </div>
    </div>
  );
}
